import React from 'react';
import { LayoutGrid, Plus, Globe, Gift, ArrowRight, Settings, Phone, Mail, Lock, Bell, Folder, FolderOpen, Pencil, Trash2, Ban, LogOut } from 'lucide-react';
import { useLanguage } from '../i18nContext';
import { FolderWithExpanded } from '../hooks/useFolders';
import MembershipStatus from './MembershipStatus';

interface DashboardSidebarProps {
    activeTab: string;
    onTabChange: (tab: string) => void;
    folders: FolderWithExpanded[];
    onCreateFolder: () => void;
    onToggleFolder: (id: string) => void;
    editingFolderId: string | null;
    editingFolderName: string;
    setEditingFolderName: (name: string) => void;
    saveEditingFolder: () => void;
    cancelEditingFolder: () => void;
    startEditingFolder: (id: string, name: string) => void;
    folderInputRef: React.RefObject<HTMLInputElement>;
    displayFolderName: (name: string) => string;

    draggingTaskId: string | null;
    dragOverFolderId: string | null;
    handleDragOverFolder: (e: React.DragEvent, folderId: string) => void;
    handleDragLeaveFolder: () => void;
    handleDropToFolder: (e: React.DragEvent, folderId: string) => void;

    setDeleteState: (state: any) => void;

    onLogout: () => void;
    onUpgrade: () => void;
    onOpenInvite: () => void;

    userInfo: { username?: string; email?: string; phone?: string } | null;
    showSettingsPopup: boolean;
    setShowSettingsPopup: (show: boolean) => void;
    setShowChangePassword: (show: boolean) => void;
    setShowBindModal: (type: 'phone' | 'email' | null) => void;
    setShowNotificationModal: (show: boolean) => void;

    onOpenWorkspace: (taskId: string) => void;
    handleDragStart: (e: React.DragEvent, taskId: string) => void;
    handleDragEnd: () => void;
    handleToggleFavorite: (taskId: string, favorite: boolean) => void;
}

export default function DashboardSidebar({
    activeTab, onTabChange, folders, onCreateFolder, onToggleFolder,
    editingFolderId, editingFolderName, setEditingFolderName, saveEditingFolder, cancelEditingFolder, startEditingFolder, folderInputRef, displayFolderName,
    draggingTaskId, dragOverFolderId, handleDragOverFolder, handleDragLeaveFolder, handleDropToFolder,
    setDeleteState, onLogout, onUpgrade, onOpenInvite,
    userInfo, showSettingsPopup, setShowSettingsPopup, setShowChangePassword, setShowBindModal, setShowNotificationModal,
    onOpenWorkspace, handleDragStart, handleDragEnd, handleToggleFavorite
}: DashboardSidebarProps) {
    const { t, language, setLanguage } = useLanguage();

    return (
        <aside className="w-72 bg-surface-light border-r border-surface-border flex flex-col fixed inset-y-0 left-0 z-30">
            <div className="h-24 flex items-center px-6">
                <div className="flex items-center gap-3 w-full group transition-all">
                    <div className="w-10 h-10 bg-brand-900 rounded-xl flex items-center justify-center text-white font-serif font-bold text-xl shadow-md group-hover:scale-105 transition-transform duration-300">M</div>
                    <div className="text-left">
                        <div className="font-serif font-bold text-base tracking-tight text-brand-900 group-hover:text-ink-dark transition-colors">Minutes AI</div>
                        <div className="text-[10px] font-medium text-ink-secondary tracking-wide uppercase">Personal Workspace</div>
                    </div>
                </div>
            </div>

            <div className="flex-1 overflow-y-auto py-4 px-4 space-y-1">
                <div className="px-3 mb-3 text-[10px] font-bold text-ink-muted uppercase tracking-widest font-sans">{t.dashboard.main}</div>
                <NavItem icon={<LayoutGrid size={18} />} label={t.dashboard.allRecordings} active={activeTab === 'all'} onClick={() => onTabChange('all')} />
                <NavItem icon={<StarIcon />} label={t.dashboard.favorites} active={activeTab === 'favorites'} onClick={() => onTabChange('favorites')} />
                <div className="flex items-center justify-between px-3 mt-10 mb-3 group">
                    <span className="text-[10px] font-bold text-ink-muted uppercase tracking-widest font-sans">{t.dashboard.folders}</span>
                    <button onClick={onCreateFolder} className="p-1 rounded-md text-ink-muted hover:bg-surface hover:text-ink-dark transition-all">
                        <Plus size={14} />
                    </button>
                </div>
                <div className="space-y-0.5">
                    {folders.map(folder => {
                        const isTaskAlreadyInFolder = draggingTaskId && folder.tasks?.some(t => t.taskId === draggingTaskId);
                        const isDragOverThis = dragOverFolderId === folder.folderId;
                        return (
                            <div key={folder.folderId}>
                                <div className="group relative">
                                    <div
                                        className={`w-full flex items-center gap-2 px-3 py-2.5 rounded-xl transition-all duration-200 mb-1 cursor-pointer ${activeTab === 'folder_' + folder.folderId
                                            ? 'bg-white text-brand-900 shadow-sm border border-surface-border font-bold'
                                            : 'text-ink-secondary hover:text-brand-900 hover:bg-surface font-medium'
                                            } ${isDragOverThis && isTaskAlreadyInFolder ? 'bg-red-50 ring-2 ring-red-300 cursor-not-allowed' : isDragOverThis ? 'bg-surface-border ring-2 ring-brand-700' : ''}`}
                                        onClick={() => { onTabChange('folder_' + folder.folderId); if (!folder.expanded) onToggleFolder(folder.folderId); }}
                                        onDragOver={(e) => handleDragOverFolder(e, folder.folderId)}
                                        onDragLeave={handleDragLeaveFolder}
                                        onDrop={(e) => handleDropToFolder(e, folder.folderId)}
                                    >
                                        <button
                                            onClick={(e) => { e.stopPropagation(); onToggleFolder(folder.folderId); }}
                                            className="p-0.5 rounded hover:bg-surface-border transition-all shrink-0"
                                        >
                                            <span className={`block transition-transform duration-200 ${folder.expanded ? 'rotate-90' : ''}`}>
                                                <ChevronRight className="text-ink-muted" />
                                            </span>
                                        </button>
                                        <span className={activeTab === 'folder_' + folder.folderId ? 'text-brand-900' : 'text-ink-muted'}>
                                            {activeTab === 'folder_' + folder.folderId || folder.expanded ? <FolderOpen size={18} className="fill-surface-border" /> : <Folder size={18} />}
                                        </span>
                                        {editingFolderId === folder.folderId ? (
                                            <input
                                                ref={folderInputRef}
                                                type="text"
                                                value={editingFolderName}
                                                onChange={(e) => setEditingFolderName(e.target.value)}
                                                onBlur={saveEditingFolder}
                                                onKeyDown={(e) => {
                                                    if (e.key === 'Enter') saveEditingFolder();
                                                    if (e.key === 'Escape') cancelEditingFolder();
                                                }}
                                                onClick={(e) => e.stopPropagation()}
                                                className="text-sm font-medium bg-white border border-surface-divider rounded px-1 py-0.5 outline-none focus:ring-1 focus:ring-brand-700 flex-1 min-w-0"
                                            />
                                        ) : (
                                            <span className="text-sm tracking-wide truncate flex-1">{displayFolderName(folder.name)}</span>
                                        )}
                                        {isDragOverThis && isTaskAlreadyInFolder ? (
                                            <Ban size={14} className="text-red-400 ml-auto shrink-0" />
                                        ) : (
                                            <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-all ml-auto">
                                                <div
                                                    onClick={(e) => { e.stopPropagation(); startEditingFolder(folder.folderId, folder.name); }}
                                                    className="p-1 hover:bg-surface-border hover:text-brand-900 rounded transition-all cursor-pointer"
                                                >
                                                    <Pencil size={12} />
                                                </div>
                                                <div
                                                    onClick={(e) => { e.stopPropagation(); setDeleteState({ isOpen: true, type: 'Folder', id: folder.folderId, title: displayFolderName(folder.name) }); }}
                                                    className="p-1 hover:bg-rose-100 hover:text-red-500 rounded transition-all cursor-pointer"
                                                >
                                                    <Trash2 size={12} />
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                                {/* 展开后显示文件夹内的任务 */}
                                {folder.expanded && (
                                    <div className="ml-7 mt-0.5 mb-1 space-y-1">
                                        {folder.tasksLoading ? (
                                            <div className="flex items-center gap-2 px-3 py-2 text-xs text-ink-muted">
                                                <div className="w-3 h-3 text-ink-muted animate-spin border-2 border-t-ink-muted rounded-full"></div>
                                                <span>Loading...</span>
                                            </div>
                                        ) : folder.tasks && folder.tasks.length > 0 ? (
                                            folder.tasks.map(task => (
                                                <div
                                                    key={task.taskId}
                                                    draggable={true}
                                                    onDragStart={(e) => {
                                                        e.stopPropagation();
                                                        handleDragStart(e, task.taskId);
                                                    }}
                                                    onDragEnd={handleDragEnd}
                                                    onClick={() => onOpenWorkspace(task.taskId)}
                                                    className={`flex items-center gap-2 px-3 py-2 rounded-lg bg-white border border-surface-border hover:border-surface-divider hover:shadow-sm cursor-grab active:cursor-grabbing transition-all group/task ${draggingTaskId === task.taskId ? 'opacity-50' : ''}`}
                                                >
                                                    <GripVertical className="text-ink-muted shrink-0 opacity-0 group-hover/task:opacity-100" />
                                                    <span className="text-xs font-medium text-brand-900 truncate flex-1">{task.title || (language === 'zh' ? '未命名' : 'Untitled')}</span>
                                                    <button
                                                        onClick={(e) => { e.stopPropagation(); handleToggleFavorite(task.taskId, !!task.isFavorite); }}
                                                        className={`p-1 rounded transition-all ${task.isFavorite ? 'text-yellow-500' : 'text-ink-muted opacity-0 group-hover/task:opacity-100 hover:text-yellow-500'}`}
                                                    >
                                                        <StarIcon filled={task.isFavorite ? 1 : 0} />
                                                    </button>
                                                </div>
                                            ))
                                        ) : (
                                            <div className="px-3 py-2 text-xs text-ink-muted italic">
                                                {t.dashboard.noTasksInFolder}
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        )
                    })}
                </div>
            </div >

            <div className="p-4 border-t border-surface-border space-y-2.5 bg-surface-light">
                <button onClick={() => setLanguage(language === 'en' ? 'zh' : 'en')} className="w-full flex items-center gap-3 px-3 py-1 rounded-xl text-ink-secondary hover:text-brand-900 hover:bg-surface transition-all text-sm font-medium">
                    <Globe size={16} />
                    <span>{language === 'en' ? 'Switch to 中文' : '切换到 English'}</span>
                </button>

                <MembershipStatus onUpgrade={onUpgrade} onViewPlan={onUpgrade} />

                <button onClick={onOpenInvite} className="w-full group relative rounded-pill transition-all duration-500 hover:scale-[1.02] active:scale-[0.98] overflow-hidden p-[1px] bg-surface-border/20 hover:bg-surface-border/40">
                    <div className="relative bg-gradient-to-br from-orange-50 to-orange-100 border border-orange-100/30 rounded-pill p-3 flex items-center gap-3 transition-all shadow-[0_2px_10px_rgba(251,146,60,0.03)] group-hover:shadow-[0_4px_15px_rgba(251,146,60,0.08)]">
                        <div className="relative w-9 h-9 shrink-0 flex items-center justify-center">
                            <div className="absolute inset-0 bg-orange-200/20 rounded-xl blur-md opacity-0 group-hover:opacity-100 transition-opacity duration-700 animate-pulse"></div>
                            <div className="relative w-full h-full bg-white border border-orange-100 rounded-xl flex items-center justify-center text-orange-500 shadow-sm group-hover:animate-float-slow transition-transform duration-500">
                                <Gift size={18} strokeWidth={2.2} />
                            </div>
                        </div>
                        <div className="text-left flex-1 min-w-0">
                            <div className="text-sm font-bold text-amber-950 tracking-tight">{t.dashboard.invite}</div>
                            <div className="text-[11px] font-medium text-orange-600/70 uppercase tracking-wider">{t.dashboard.inviteSub}</div>
                        </div>
                        <div className="w-6 h-6 rounded-full bg-white/50 flex items-center justify-center text-orange-400 opacity-0 group-hover:opacity-100 group-hover:translate-x-0 -translate-x-1 transition-all duration-300">
                            <ArrowRight size={12} strokeWidth={2.5} />
                        </div>
                    </div>
                </button>

                <div className="flex items-center gap-2">
                    <div className="relative settings-popup-container">
                        <button
                            onClick={(e) => { e.stopPropagation(); setShowSettingsPopup(!showSettingsPopup); }}
                            className="w-8 h-8 rounded-full bg-surface-border flex items-center justify-center border border-surface-divider shrink-0 shadow-sm hover:bg-surface-divider transition-colors"
                        >
                            <Settings size={16} className="text-ink-secondary" />
                        </button>

                        {showSettingsPopup && (
                            <div className="absolute bottom-full left-0 mb-2 w-56 bg-white border border-surface-border rounded-2xl shadow-xl z-50 overflow-hidden animate-in fade-in zoom-in-95 duration-150 p-1">
                                <div
                                    onClick={() => { if (!userInfo?.phone) { setShowBindModal('phone'); setShowSettingsPopup(false); } }}
                                    className={`w-full flex items-center gap-3 px-4 py-3 text-sm font-medium rounded-xl transition-colors text-left ${userInfo?.phone
                                        ? 'text-ink-muted bg-surface'
                                        : 'text-brand-900 hover:bg-surface cursor-pointer'
                                        }`}
                                >
                                    <Phone size={16} className="text-ink-muted" />
                                    {language === 'zh' ? '绑定手机号' : 'Bind Phone'}
                                    {userInfo?.phone && <span className="ml-auto text-xs text-green-500">✓</span>}
                                </div>
                                <div
                                    onClick={() => { if (!userInfo?.email) { setShowBindModal('email'); setShowSettingsPopup(false); } }}
                                    className={`w-full flex items-center gap-3 px-4 py-3 text-sm font-medium rounded-xl transition-colors text-left ${userInfo?.email
                                        ? 'text-ink-muted bg-surface'
                                        : 'text-brand-900 hover:bg-surface cursor-pointer'
                                        }`}
                                >
                                    <Mail size={16} className="text-ink-muted" />
                                    {language === 'zh' ? '绑定邮箱' : 'Bind Email'}
                                    {userInfo?.email && <span className="ml-auto text-xs text-green-500">✓</span>}
                                </div>
                                <div className="h-px bg-surface-border mx-2" />
                                <div
                                    onClick={() => {
                                        if ('Notification' in window && Notification.permission === 'granted') return;
                                        setShowNotificationModal(true);
                                        setShowSettingsPopup(false);
                                    }}
                                    className={`w-full flex items-center gap-3 px-4 py-3 text-sm font-medium rounded-xl transition-colors text-left ${'Notification' in window && Notification.permission === 'granted'
                                        ? 'text-ink-muted bg-surface'
                                        : 'text-brand-900 hover:bg-surface cursor-pointer'
                                        }`}
                                >
                                    <Bell size={16} className="text-ink-muted" />
                                    {language === 'zh' ? '任务通知' : 'Notifications'}
                                    {'Notification' in window && Notification.permission === 'granted' && <span className="ml-auto text-xs text-green-500">✓</span>}
                                </div>
                                <div className="h-px bg-surface-border mx-2" />
                                <div
                                    onClick={() => { setShowChangePassword(true); setShowSettingsPopup(false); }}
                                    className="w-full flex items-center gap-3 px-4 py-3 text-sm font-medium rounded-xl transition-colors text-left text-brand-900 hover:bg-surface cursor-pointer"
                                >
                                    <Lock size={16} className="text-ink-muted" />
                                    {language === 'zh' ? '修改密码' : 'Change Password'}
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="flex-1 min-w-0">
                        <div className="text-sm font-bold text-brand-900 truncate">{userInfo?.username || (language === 'zh' ? '用户' : 'User')}</div>
                        <div className="text-xs text-ink-secondary truncate font-medium">{userInfo?.email || userInfo?.phone || ''}</div>
                    </div>

                    <button onClick={onLogout} className="p-2 rounded-lg hover:bg-surface transition-colors group">
                        <LogOut size={16} className="text-ink-muted group-hover:text-ink-dark" />
                    </button>
                </div>
            </div>
        </aside >
    );
}

function NavItem({ icon, label, active = false, onClick }: { icon: React.ReactNode, label: string, active?: boolean, onClick?: () => void }) {
    return (
        <button onClick={onClick} className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all duration-200 mb-1 ${active ? 'bg-white text-brand-900 shadow-sm border border-surface-border font-bold' : 'text-ink-secondary hover:text-brand-900 hover:bg-surface font-medium'}`}>
            <span className={active ? 'text-brand-900' : 'text-ink-muted'}>{icon}</span>
            <span className="text-sm tracking-wide">{label}</span>
        </button>
    );
}
function StarIcon({ filled }: { filled?: number }) { return <svg width="18" height="18" viewBox="0 0 24 24" fill={filled ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"></polygon></svg>; }
function GripVertical(props: any) { return <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}><circle cx="9" cy="12" r="1" /><circle cx="9" cy="5" r="1" /><circle cx="9" cy="19" r="1" /><circle cx="15" cy="12" r="1" /><circle cx="15" cy="5" r="1" /><circle cx="15" cy="19" r="1" /></svg>; }
function ChevronRight(props: any) { return <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}><polyline points="9 18 15 12 9 6"></polyline></svg>; }
